import { useRef } from 'react'

import { useScrollProgress } from '@/hooks/useScrollProgress'
import { ease, lerp, ramp } from '@/lib/scroll'

// A row arrives over the first three quarters of a screen after its top enters.
const rowProgress = (el) =>
  (window.innerHeight - el.getBoundingClientRect().top) /
  (window.innerHeight * 0.75)

/**
 * One panel, number `n`: its picture beside its text. Once the row
 * is wide enough for both side by side (648px: two 300px halves and the gap),
 * the picture swaps sides on every other row; narrower, they stack with the
 * picture first. The row comes into focus, from small and faded, as it
 * scrolls in, and from 900px up also from blurred. Phones and tablets skip
 * the blur: on iPhone Safari a blur filter on a moving element can leave a
 * coloured line along its edge. Once in focus the row carries no filter.
 */
export default function PanelRow({ panel, n }) {
  const row = useRef(null)
  const p = useScrollProgress(row, rowProgress)
  const shown = ease(ramp(p, 0, 0.85))

  return (
    <div
      ref={row}
      className={`@container flex flex-wrap items-center gap-x-12 gap-y-8 ${shown < 1 ? 'md:blur-(--blur)' : ''}`}
      style={{
        opacity: shown,
        transform: `scale(${lerp(0.94, 1, shown)})`,
        '--blur': `${(1 - shown) * 6}px`,
      }}
    >
      {/* The screen and its floating cards, on a transparent background, shown
          whole at its own proportions (the size reserves its space). */}
      <img
        src={panel.image}
        alt={panel.title}
        width={1536}
        height={1120}
        loading="lazy"
        decoding="async"
        className={`h-auto min-w-0 flex-[1_1_300px] ${n % 2 ? '' : '@min-[648px]:order-2'}`}
      />

      <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-[18px]">
        <span className="flex items-center gap-2 text-[13px] font-medium text-muted">
          {/* The number goes dark once its row is past halfway in. */}
          <span
            className={`grid size-7 place-items-center rounded-full text-xs font-semibold transition-colors duration-300 ${p > 0.55 ? 'bg-ink text-white' : 'bg-[#dfe3ea] text-subtle'}`}
          >
            {n}
          </span>
          {panel.tag}
        </span>
        <h3 className="text-[clamp(26px,2.6vw,34px)] leading-[1.15] tracking-[-0.02em]">
          {panel.title}
        </h3>
        <p className="max-w-110 text-[15px]/[1.7] text-muted">{panel.text}</p>
        <div className="flex max-w-110 flex-col gap-1.5">
          {panel.rows.map((row) => (
            <span key={row.label} className="text-sm/[1.6]">
              <span className="text-subtle">{row.label}:</span> {row.value}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
